import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/slw/shared/ui/dialog';
import { Button } from '@/slw/shared/ui/button';
import { Input } from '@/slw/shared/ui/input';
import { onSaveSearch } from '@/slw/entities/search-results/api-client.api';
import { ListingsFilter } from '@/slw/entities/search-results/search-results.type';
import { useSaveSearchQuery } from '@/slw/entities/search-results/search-results.query';
import { queryClient } from '@/slw/shared/query-client';
import { formatSaveNumber } from '@/slw/shared/ui/form/input';

export interface Alert {
  id: number;
  name: string;
}

export interface SaveSearchModalProps {
  open: boolean;
  onClose: () => void;
  filter: ListingsFilter;
}

export function SaveSearchModal({ open, onClose, filter }: SaveSearchModalProps) {
  const [alertName, setAlertName] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState<number | null>(null);
  const [isPending, setIsPending] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const savedSearch = useSaveSearchQuery();

  useEffect(() => {
    if (!open) {
      setAlertName('');
      setSelectedAlertId(null);
      setIsDropdownOpen(false);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // if (existingAlerts.length > 0) setIsDropdownOpen(true);
  };

  const handleAlertSelect = (alertId: number) => {
    const alert = (savedSearch.data?.data || []).find((a) => a.id === alertId);

    if (alert && alert.id) {
      setAlertName(alert.name);
      setSelectedAlertId(alert.id);
    }

    setIsDropdownOpen(false);
  };

  const handleSaveClick = async () => {
    if (!alertName.trim()) {
      inputRef.current?.focus();

      return;
    }

    setIsPending(true);
    try {
      await onSaveSearch({
        id: selectedAlertId || undefined,
        name: alertName.trim(),
        carFilter: {
          ...filter,
          fromYear: filter.fromYear ? formatSaveNumber(filter.fromYear) : undefined,
          toYear: filter.toYear ? formatSaveNumber(filter.toYear) : undefined,
          fromPrice: filter.fromPrice ? formatSaveNumber(filter.fromPrice) : undefined,
          toPrice: filter.toPrice ? formatSaveNumber(filter.toPrice) : undefined,
          fromOdometer: filter.fromOdometer ? formatSaveNumber(filter.fromOdometer) : undefined,
          toOdometer: filter.toOdometer ? formatSaveNumber(filter.toOdometer) : undefined,
        } as any,
      });
      toast.success('Save successfully');
      queryClient.invalidateQueries({ queryKey: ['SAVE_YOUR_SEARCH'] });
      setAlertName('');
      setSelectedAlertId(null);
      setIsDropdownOpen(false);
      onClose();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save your save');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='p-8 max-w-[540px]! w-full rounded-md'>
        <DialogHeader>
          <DialogTitle className='text-2xl font-bold leading-[150%] mb-3'>New Alert</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveClick();
          }}
        >
          <div className='mb-4'>
            <label htmlFor='alertName' className='block mb-2 text-sm font-medium text-gray-700'>
              Alert name<span className='text-[#dc2626]'>*</span>
            </label>
            <div className='relative' ref={dropdownRef}>
              <div className='relative '>
                <Input
                  ref={inputRef}
                  type='text'
                  id='alertName'
                  value={alertName}
                  onChange={(e) => {
                    setAlertName(e.target.value);
                    setSelectedAlertId(null);
                  }}
                  onClick={handleInputClick}
                  className='w-full'
                  placeholder='Type a new name...'
                  required
                  disabled={isPending}
                />
                <button
                  type='button'
                  className='absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500'
                  onClick={(e) => {
                    e.preventDefault();
                    setIsDropdownOpen((v) => !v);
                  }}
                  tabIndex={-1}
                >
                  <ChevronDown size={18} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>
              {isDropdownOpen && (savedSearch.data?.data || []).length > 0 && (
                <div
                  className='absolute z-10 w-full mt-1 bg-white rounded-md border
                 border-gray-200 shadow-lg max-h-60 overflow-auto animate-slideDown'
                >
                  <div className='py-1 text-sm text-gray-500 px-3 border-b'>
                    <div className='flex items-center gap-1 py-1'>
                      <AlertCircle size={16} />
                      <span>Select to override an existing alert</span>
                    </div>
                  </div>
                  <ul>
                    {savedSearch.data?.data.map((alert) => (
                      <li key={alert.id}>
                        <button
                          type='button'
                          className='w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors'
                          onClick={() => handleAlertSelect(alert.id!)}
                        >
                          {alert.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className='text-xs text-[#979696] mb-4'>
            You can find your Saved Search in My Alerts. Click on your profiles
          </div>

          <Button type='submit' className='w-full' disabled={!alertName.trim() || isPending}>
            <span>SAVE</span>
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
